import axios from 'axios';
import { API_BASE_URL } from './constants';

/**
 * Axios instance for the VisionAI Admin API.
 * Automatically attaches Bearer token from localStorage.
 */
export const adminApi = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000, // 60 s for normal requests
    headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: inject auth token ────────────────────────────────
adminApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ── Response interceptor: normalise errors ────────────────────────────────
adminApi.interceptors.response.use(
    (response) => response,
    (error) => {
        const message =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'An unexpected error occurred.';
        const enhancedError = new Error(message);
        enhancedError.status = error.response?.status;
        enhancedError.data = error.response?.data;
        return Promise.reject(enhancedError);
    }
);

// ── Auth ──────────────────────────────────────────────────────────────────
export const authAPI = {
    login: (email, password) => adminApi.post('/api/auth/login', { email, password }),
    register: (name, email, password) => adminApi.post('/api/auth/register', { name, email, password }),
    me: () => adminApi.get('/api/auth/me'),
    logout: () => adminApi.post('/api/auth/logout'),
};

// ── Menus ─────────────────────────────────────────────────────────────────
export const menuAPI = {
    getAll: () => adminApi.get('/api/menus'),
    getById: (id) => adminApi.get(`/api/menus/${id}`),
    create: (data) => adminApi.post('/api/menus', data),
    update: (id, data) => adminApi.put(`/api/menus/${id}`, data),
    delete: (id) => adminApi.delete(`/api/menus/${id}`),
};

// ── Models ────────────────────────────────────────────────────────────────
export const modelAPI = {
    getAll: (params = {}) => adminApi.get('/api/models', { params }),
    getById: (id) => adminApi.get(`/api/models/${id}`),
    getStats: () => adminApi.get('/api/models/stats'),
    /** Check whether a folder for the given model name already exists on disk. */
    checkName: (name) => adminApi.get('/api/models/check-name', { params: { name } }),
    upload: (formData, config = {}) =>
        adminApi.post('/api/models', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10 * 60 * 1000, // 10 minutes for large model uploads
            ...config,
        }),
    update: (id, formData) =>
        adminApi.put(`/api/models/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10 * 60 * 1000, // 10 minutes for large model updates
        }),
    delete: (id) => adminApi.delete(`/api/models/${id}`),
};

// ── Users ─────────────────────────────────────────────────────────────────
export const userAPI = {
    getAll: (params = {}) => adminApi.get('/api/users', { params }),
    getById: (id) => adminApi.get(`/api/users/${id}`),
    update: (id, data) => adminApi.put(`/api/users/${id}`, data),
    delete: (id) => adminApi.delete(`/api/users/${id}`),
    getStats: () => adminApi.get('/api/users/stats'),
};
